import { isDef, tryOnScopeDispose, useTimeoutFn } from '@vueuse/shared'
import { onSnapshot, DocumentReference, type DocumentData, type DocumentSnapshot, type Query, type QuerySnapshot } from 'firebase/firestore'
import { computed, ref as deepRef, isRef, watch, type Ref, type ComputedRef } from 'vue'

import { useLogger } from '@booksy-com/vue-tools'

import {
  toNumberIfNumeric,
} from './utils'

const logger = useLogger('useFirestore')

function getData (docRef: DocumentSnapshot<DocumentData>): DocumentData | undefined {
  const data = docRef.data()

  if (data) {
    Object.defineProperty(data, 'id', {
      value: toNumberIfNumeric(docRef.id.toString()),
      writable: false,
    })
  }
  return data
}

function getChanges (snapshot: QuerySnapshot<DocumentData>) {
  return snapshot.docChanges().map(change => ({
    type: change.type,
    doc: getData(change.doc),
  }))
}

function isDocumentReference (docRef: unknown): docRef is DocumentReference {
  return docRef instanceof DocumentReference
}

export type FirestoreListenTarget = Query | DocumentReference | undefined | null | false

export type FirestoreListenSource =
  | Ref<FirestoreListenTarget>
  | ComputedRef<FirestoreListenTarget>
  | FirestoreListenTarget

export interface UseFirestoreOptions {
  errorHandler?: (err: unknown) => void
  autoDispose?: boolean | number
}

export function useFirestore (
  maybeDocRef: FirestoreListenSource,
  initialValue?: unknown,
  options?: UseFirestoreOptions
): {
  data: Ref<unknown>
  changes: Ref<unknown>
} {
  const {
    errorHandler = (err: unknown) => logger.error('Firestore error:', err),
    autoDispose = true,
  } = options || {}

  const refOfDocRef = isRef(maybeDocRef)
    ? maybeDocRef as Ref<FirestoreListenTarget>
    : computed(() => maybeDocRef as FirestoreListenTarget)

  let close = () => { }
  const data = deepRef(initialValue)
  const changes = deepRef()

  watch(refOfDocRef, (docRef) => {
    close()
    if (!refOfDocRef.value) {
      data.value = initialValue
    } else if (isDocumentReference(refOfDocRef.value)) {
      close = onSnapshot(docRef as DocumentReference<DocumentData>, (snapshot) => {
        data.value = getData(snapshot) || null
      }, errorHandler)
    } else {
      close = onSnapshot(docRef as Query<DocumentData>, (snapshot) => {
        data.value = snapshot.docs.map(getData).filter(isDef)
        changes.value = getChanges(snapshot)
      }, errorHandler)
    }
  }, { immediate: true })

  if (autoDispose === true) {
    // Dispose the request now.
    tryOnScopeDispose(() => {
      close()
    })
  } else if (typeof autoDispose === 'number') {
    // Dispose the request after timeout.
    tryOnScopeDispose(() => {
      useTimeoutFn(() => {
        close()
      }, autoDispose)
    })
  }

  return { data, changes }
}
